import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import ProgressCircle from '@uswitch/ev-components/lib/components/elements/progress-circle'
import { useComponentTracking } from '../../../utils/component-events'
import BusinessFinder from './business-finder'
import { Field, Label, Radio, RadioGroup, Button } from '../../../atoms'
import { readCookieRaw } from '../../../utils/cookies'
import { brand } from '../../../redux/app-selectors'
import { FsCore } from '../../../types/fs-core'
import { setConfig, updateConsents } from '@uswitch/rvuconsent'
import { omit } from 'lodash-es'
import { useForm } from 'react-hook-form'

interface SMEformProps {
  brand: 'money' | 'uswitch' | 'confused'
  sys: {
    id: string
  }
  name: string
}

const getChosenBundle = (businessDetails) => {
  if (businessDetails.isExistingBusiness === 'no') {
    return 'startup'
  } else {
    const existenceBundleMap = {
      'less than 1 year': 'startup',
      '1-5 years': 'mid',
      '5+ years': 'large',
    }
    return existenceBundleMap[businessDetails.existenceTime]
  }
}

const sendConsents = (businessDetails) => {
  return updateConsents({
    email: businessDetails.email,
    consents: [
      {
        name: 'sme-consent',
        status: true,
        source: 'sme-journey',
        sourceUrl: window.location.href,
      },
    ],
  })
}

const sendForm = async ({ brand, businessDetails, newJourney }) => {
  const chosenBundle = getChosenBundle(businessDetails)
  const finalPayload = {
    ...businessDetails,
    physicalPremises: businessDetails.physicalPremises === 'yes',
    address: {
      address_line_1: businessDetails.address_line_1,
      ...(businessDetails.address_line_2 && { address_line_2: businessDetails.address_line_2 }),
      postal_code: businessDetails.postal_code,
      locality: businessDetails.locality,
      country: businessDetails.country,
    },
    selectedBundle: !newJourney ? chosenBundle : 'dynamic',
  }

  await fetch(`${__FS_API_URL__}/form/sme?brand=${brand}&deviceId=${readCookieRaw('uscd')}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(finalPayload),
  })
}

const getBundleHref = (businessDetails) => {
  const chosenBundle = getChosenBundle(businessDetails)
  const bundleHrefMap = {
    startup: '/business/journey/results/startup',
    mid: '/business/journey/results/scaling',
    large: '/business/journey/results/sustaining',
  }

  return bundleHrefMap[chosenBundle]
}

const SMEform: React.FC<SMEformProps> = ({ brand, sys, name }) => {
  const trackComponent = useComponentTracking()

  const { register, handleSubmit, errors, setValue, watch, getValues } = useForm({
    mode: 'all',
    defaultValues: { businessDetails: {} },
  })

  const businessDetails = watch('businessDetails')

  const [consents, setConsents] = useState({ marketing: false })
  const [validationError, setValidationError] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(false)

  setConfig({
    apiPath: 'https://www.money.co.uk/consent',
    clientId: 'fs-core',
    brand: 'money',
  })

  const componentData = {
    component_category: 'component',
    component_id: sys.id,
    component_name: 'sme_form',
    contentful_name: name,
    contentful_variant: 'sme_form',
  }

  const onBusinessDetailsChange = (e: React.ChangeEvent<HTMLInputElement>, detail: string) => {
    setValue(`businessDetails.${detail}`, e.target.value)
    trackComponent('Dynamic Component Event', 'FormStepChange', {
      formStep: detail,
      selected: e.target.value,
    })
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlSearchParams = new URLSearchParams(window.location?.search)
      const landingPage = urlSearchParams.get('landingPage')
      if (landingPage) {
        sessionStorage.setItem('landingPage', landingPage)
      }
    }
  }, [])

  return (
    <div className='SMEform'>
      <h3>Tell us about your business</h3>
      <form>
        <Field className='SMEform__typeOfBusiness'>
          <Label>Is your business up and running?</Label>
          <div className='SMEform__input'>
            <RadioGroup column={false}>
              <Radio
                name='exisitngBusiness'
                value='yes'
                currentValue={businessDetails.isExistingBusiness}
                onChange={(e) => {
                  onBusinessDetailsChange(e, 'isExistingBusiness')
                }}
                {...register('businessDetails.isExistingBusiness', {
                  required: 'Please tell us if you are an existing business',
                })}
              >
                Yes
              </Radio>
              <Radio
                name='exisitngBusiness'
                value='no'
                currentValue={businessDetails.isExistingBusiness}
                onChange={(e) => {
                  onBusinessDetailsChange(e, 'isExistingBusiness')
                }}
                {...register('businessDetails.isExistingBusiness', {
                  required: 'Please tell us if you are an existing business',
                })}
              >
                No
              </Radio>
            </RadioGroup>
            {errors?.businessDetails?.isExistingBusiness && (
              <div className='SMEform__error'>
                {errors?.businessDetails?.isExistingBusiness.message}
              </div>
            )}
          </div>
        </Field>
        {businessDetails.isExistingBusiness === 'yes' && (
          <Field className='SMEform__typeOfBusiness'>
            <Label>Are you a...</Label>
            <div className='SMEform__input'>
              <RadioGroup column={true}>
                {[
                  { name: 'soleTrader', text: 'Sole Trader' },
                  { name: 'limitedCompany', text: 'Limited Company' },
                  { name: 'partnership', text: 'Partnership' },
                  { name: 'notsetup', text: 'Not set up' },
                ].map(({ name, text }, index) => {
                  return (
                    <Radio
                      name={name}
                      value={name}
                      currentValue={businessDetails.businessType}
                      onChange={(e) => {
                        onBusinessDetailsChange(e, 'businessType')
                      }}
                      key={index}
                      {...register('businessDetails.businessType', {
                        required: 'Please tell us your business type',
                      })}
                    >
                      {text}
                    </Radio>
                  )
                })}
              </RadioGroup>
              {errors?.businessDetails?.businessType && (
                <div className='SMEform__error'>
                  {errors?.businessDetails?.businessType.message}
                </div>
              )}
            </div>
          </Field>
        )}
        {businessDetails.isExistingBusiness === 'yes' && businessDetails.businessType && (
          <BusinessFinder
            businessDetails={businessDetails}
            brand={brand}
            disable={businessDetails.businessType === 'notsetup'}
            componentData={componentData}
            register={register}
            setValue={setValue}
            errors={errors}
            getValues={getValues}
            soleTrader={businessDetails.businessType === 'soleTrader'}
          />
        )}
        {businessDetails.isExistingBusiness === 'yes' &&
          businessDetails.businessType === 'soleTrader' && (
            <Field className='SMEform__typeOfBusiness'>
              <Label>How old is your business?</Label>
              <div className='SMEform__input'>
                <RadioGroup column={true}>
                  <Radio
                    name='less than 1 year'
                    value='less than 1 year'
                    currentValue={businessDetails.existenceTime}
                    onChange={(e) => {
                      onBusinessDetailsChange(e, 'existenceTime')
                    }}
                    {...register('businessDetails.existenceTime', {
                      required: 'Please tell us how old your business is',
                    })}
                  >
                    Less than 1 year old
                  </Radio>
                  <Radio
                    name='1-5 years'
                    value='1-5 years'
                    currentValue={businessDetails.existenceTime}
                    onChange={(e) => {
                      onBusinessDetailsChange(e, 'existenceTime')
                    }}
                    {...register('businessDetails.existenceTime', {
                      required: 'Please tell us how old your business is',
                    })}
                  >
                    1-5 years
                  </Radio>
                  <Radio
                    name='5+ years'
                    value='5+ years'
                    currentValue={businessDetails.existenceTime}
                    onChange={(e) => {
                      onBusinessDetailsChange(e, 'existenceTime')
                    }}
                    {...register('businessDetails.existenceTime', {
                      required: 'Please tell us how old your business is',
                    })}
                  >
                    5+ years
                  </Radio>
                  {errors?.businessDetails?.existenceTime && (
                    <div className='SMEform__error'>
                      {errors?.businessDetails?.existenceTime.message}
                    </div>
                  )}
                </RadioGroup>
              </div>
            </Field>
          )}
        {businessDetails.isExistingBusiness && (
          <Field className='SMEform__typeOfBusiness'>
            <Label>Select the best type that describes your business</Label>
            <div className='SMEform__input'>
              <select
                onChange={(e) => {
                  onBusinessDetailsChange(e, 'industry')
                }}
                name='industry'
                {...register('businessDetails.industry', {
                  required: 'Please tell us what industry you are in',
                })}
              >
                <option value={''}>Please select</option>
                <option value={'Professional Services'}>Professional Services</option>
                <option value={'Property Services'}>Property Services</option>
                <option value={'Building & Trade'}>Building & Trade</option>
                <option value={'Retail'}>Retail</option>
                <option value={'Transport'}>Transport</option>
                <option value={'Other'}>Other</option>
              </select>
              {errors?.businessDetails?.industry && (
                <div className='SMEform__error'>{errors?.businessDetails?.industry.message}</div>
              )}
            </div>
          </Field>
        )}
        {businessDetails.isExistingBusiness === 'yes' && businessDetails.businessType && (
          <Field className='SMEform__typeOfBusiness'>
            <Label>
              Does your business have a physical location or premises, such as an office or
              warehouse?
            </Label>
            <div className='SMEform__input'>
              <RadioGroup column={false}>
                <Radio
                  name='physicalPremises'
                  value='yes'
                  currentValue={businessDetails.physicalPremises}
                  onChange={(e) => {
                    onBusinessDetailsChange(e, 'physicalPremises')
                  }}
                  {...register('businessDetails.physicalPremises', {
                    required: 'Please tell us if your business has a physical location',
                  })}
                >
                  Yes
                </Radio>
                <Radio
                  name='physicalPremises'
                  value='no'
                  currentValue={businessDetails.physicalPremises}
                  onChange={(e) => {
                    onBusinessDetailsChange(e, 'physicalPremises')
                  }}
                  {...register('businessDetails.physicalPremises', {
                    required: 'Please tell us if your business has a physical location',
                  })}
                >
                  No
                </Radio>
              </RadioGroup>
              {errors?.businessDetails?.physicalPremises && (
                <div className='SMEform__error'>
                  {errors?.businessDetails?.physicalPremises.message}
                </div>
              )}
            </div>
          </Field>
        )}
        <Field className='SMEform__emailAddress'>
          <Label htmlFor='email'>Contact email address</Label>
          <div className='SMEform__input'>
            <div>We will use your email to send you your results</div>
            <input
              type='email'
              id='email'
              placeholder='Email address'
              onChange={(e) => {
                setTimeout(() => {
                  const validEmail = e.target.value.toLowerCase().match(/@/)
                  if (validEmail) {
                    onBusinessDetailsChange(e, 'email')
                  }
                }, 500)
              }}
              {...register('businessDetails.email', {
                required: 'Please enter your email address',
              })}
            />
            <div className='SMEform__marketingConsent'>
              <input
                type='checkbox'
                id='marketingConsent'
                name='marketingConsent'
                value='marketingConsent'
                onChange={(e) => {
                  setConsents({ ...consents, marketing: e.target.checked })
                }}
              />
              <div>Yes, I&apos;d like to receive money-saving deals and expert insights</div>
            </div>
            {errors.businessDetails?.email && (
              <div className='SMEform__error'>{errors.businessDetails.email?.message}</div>
            )}
          </div>
        </Field>

        <Button
          variant='primary'
          size='lg'
          dataClickTracking={{
            component_category: 'component',
            component_id: sys?.id,
            component_name: 'sme-form',
            contentful_name: name,
            contentful_variant: '',
            additionalProps: {
              text: 'Submit',
              ...omit(businessDetails, ['email', 'address']),
            },
          }}
          onClick={handleSubmit(async (data) => {
            try {
              setLoading(true)

              if (consents.marketing) {
                await sendConsents(data.businessDetails)
              }
              const newJourney = JSON.parse(window.__NEW_SME_JOURNEY__)
              await sendForm({
                brand,
                newJourney,
                businessDetails: data.businessDetails,
              })

              setLoading(false)

              window.location.href = !newJourney
                ? getBundleHref(data.businessDetails)
                : '/business/journey/results'
            } catch (e) {
              setLoading(false)
              setValidationError('Oops looks like something went wrong. Please try again later')
            }
          })}
        >
          Submit {loading && <ProgressCircle />}
        </Button>
        {validationError && <div className='SMEform__error'>{validationError}</div>}
      </form>
    </div>
  )
}

export default connect((state: FsCore.State, props: any) => {
  return {
    brand: brand(state),
    ...props,
  }
})(SMEform)
